/**
 * scheduleTaskGeneration.ts
 * Frontend service for the "Generate Tasks" action — Phase 2, Item 2.
 *
 * All API calls go through apiClient (axios + MSAL bearer + base URL).
 * Endpoints:
 *   POST /api/schedule/generate-tasks
 */

import apiClient from './apiClient';
import { toScenarioLockedError } from './scheduleAssignments';

export interface GenerateTasksResult {
  tasksCreated: number;
  tasksUpdated: number;
  bookingsCreated: number;
  /** Names of TaskTypes for which no capable machine was found. */
  unplacedTaskTypes: string[];
  /** True when the source has ≥1 rock test (drives hidden prep steps). */
  hasRockTest?: boolean;
  /** Number of hidden prep ScheduleTasks newly inserted this run. */
  prepStepsCreated?: number;
  /**
   * Source lines that could not be mapped to a scheduling catalog item (or whose
   * catalog item has no task-type mapping) and were therefore skipped — surfaced
   * as a warning so a missing test never goes unnoticed.
   */
  unmappedTests?: string[];
  /**
   * Phase 3 (Test↔Catalog mapping): source lines that DID schedule, but only
   * via an UNCONFIRMED TestCatalogMap estimate the lab has not blessed yet —
   * milder than unmappedTests (the tasks exist). Hand-mirrored from backend
   * GenerateTasksResult (the two types can drift — keep in sync).
   */
  unconfirmedTests?: string[];
  /**
   * Migration 111: source lines skipped because their catalog item has
   * "Include in Schedule" unchecked (an admin deliberately opted the test out
   * of scenario planning). Informational, not a warning.
   */
  excludedTests?: string[];
  /**
   * G2-8 (audit #2): present when the requested projectStart was in the PAST —
   * the server floors placement at today (machines cannot be booked on dates
   * already gone). Surface the note so the user knows their date was moved.
   */
  projectStartClamped?: { requested: string; effective: string };
  /**
   * RM-2 (Run Mode hardening): task-type names whose RunMode flip was NOT
   * adopted because other scenarios pin the shared task's sizing (P2-3) —
   * placement used the pinned snapshot mode. Release/replan the pinning
   * scenarios, then re-sync, to adopt the flip.
   */
  runModePinnedTasks?: string[];
}

export interface GenerateTasksRequest {
  source: 'Quote' | 'WorkOrder';
  sourceKey: string;
  /**
   * FEAT-2026-0007 (6B.3): optional scenario under which to record the new
   * MachineBooking rows.  Defaults to 'BASELINE' on the backend when omitted.
   */
  scenarioKey?: string;
  /**
   * G-2 short-term: forwarded to the auto-placer; omitted = historical default
   * (use_defaults). AutoScheduleStrategy is declared below in this module.
   */
  strategy?: AutoScheduleStrategy;
  /**
   * G-2 short-term: 'YYYY-MM-DD' placement floor forwarded to the auto-placer;
   * omitted = historical default (placed from today).
   */
  projectStart?: string;
}

/**
 * POST /api/schedule/generate-tasks
 *
 * Materialises ScheduleTask rows from a Quote or WorkOrder's line items.
 * Idempotent: re-running updates existing tasks instead of inserting duplicates.
 *
 * @param request - { source: 'Quote' | 'WorkOrder', sourceKey: UUID }
 * @returns summary counts and any task types that could not be placed on a machine
 */
export async function generateScheduleTasks(
  request: GenerateTasksRequest,
): Promise<GenerateTasksResult> {
  try {
    const response = await apiClient.post<GenerateTasksResult>(
      '/schedule/generate-tasks',
      request,
    );
    return response.data;
  } catch (err: unknown) {
    // P2-8: generating into a Submitted plan 409s scenario_locked (P2-2) —
    // surface the server's "Reopen it…" message, not a raw axios 409.
    const locked = toScenarioLockedError(err);
    if (locked) throw locked;
    throw err;
  }
}

/** Durable mapping-health readout for one source (BE SourceMappingHealth). */
export interface SourceMappingHealth {
  /** Lines generation skips entirely — the loud "not schedulable" list. */
  unmappedTests: string[];
  /** Lines that schedule via an unconfirmed mapping estimate — milder advisory. */
  unconfirmedTests: string[];
}

/**
 * GET /api/schedule/unmapped-tests — G-4 (audit 2026-07-01): recomputable
 * "not schedulable" check. Returns the source lines that have no catalog
 * mapping (and therefore created NO ScheduleTask rows) — durable counterpart
 * of GenerateTasksResult.unmappedTests, so the warning survives reloads.
 * Phase 3: also carries unconfirmedTests (lines scheduling via an unconfirmed
 * TestCatalogMap estimate); tolerant of an older backend omitting the field.
 */
export async function fetchUnmappedTests(
  source: 'Quote' | 'WorkOrder',
  sourceKey: string,
): Promise<SourceMappingHealth> {
  const response = await apiClient.get<Partial<SourceMappingHealth>>(
    '/schedule/unmapped-tests',
    { params: { source, sourceKey } },
  );
  return {
    unmappedTests: response.data.unmappedTests ?? [],
    unconfirmedTests: response.data.unconfirmedTests ?? [],
  };
}

// ---------------------------------------------------------------------------
// Auto-schedule re-plan + undo ("Set" with a strategy & project start date)
// ---------------------------------------------------------------------------

/** Default machines (A) vs minimize span / multi-machine (B). */
export type AutoScheduleStrategy = 'use_defaults' | 'minimize_span';

/** A captured booking, round-tripped so the client can offer Undo. */
export interface BookingSnapshotRow {
  /** P-6: the original booking key — lets Undo re-point staffing exactly. */
  machineBookingKey: string;
  scheduleTaskKey: string;
  machineKey: string;
  startDate: string;
  endDate: string;
  status: string;
  source: string;
  /** P2-12: the booking's per-machine share (mirrors the backend token). */
  splitQty?: number | null;
}

/** P-6: a captured EmployeeAssignment row (hand-mirrored from
 *  backend scheduleReplanStaffing.ts — copy field names EXACTLY). */
export interface AssignmentSnapshotRow {
  machineBookingKey: string;
  scheduleTaskKey: string;
  userKey: string;
  weekStart: string;
  percent: number;
  status: string;
  source: string;
  notes: string | null;
  startDate: string | null;
  endDate: string | null;
}

export interface ReplanUndo {
  scenarioKey: string;
  bookings: BookingSnapshotRow[];
  /** RM-8: the optional sizing trio (preset/minutes/runMode) rides beside
   *  MachineCount so Undo restores a run-mode-adopting replan completely —
   *  hand-mirrored from backend scheduleReplanService.TaskCountSnapshotRow. */
  taskCounts: {
    scheduleTaskKey: string;
    machineCount: number;
    presetDurationDays?: number;
    machineMinutes?: number | null;
    runMode?: string;
  }[];
  /** P-6: the cleared bookings' staffing rows. */
  assignments: AssignmentSnapshotRow[];
}

export interface ReplanResponse {
  result: GenerateTasksResult;
  undo: ReplanUndo;
  /** P-6: how the pre-replan staffing fared through the re-attach. */
  staffing: { staffingReattached: number; staffingDropped: number };
  /**
   * G2-5 (audit #2): committed bookings the Tentative-only re-plan kept at
   * their dates (count + distinct task names) — surfaced in the summary so a
   * "0 placed" result on committed work is explained, not mysterious.
   * Optional so a stale backend during rollout doesn't break the flash.
   */
  committedKept?: { count: number; taskNames: string[] };
}

/**
 * POST /api/schedule/replan — re-plan a scenario per the chosen strategy, anchored
 * at the project start date. Clears the scenario's Tentative bookings first, so a
 * strategy switch fully re-places. Returns the new counts + an undo snapshot.
 */
export async function replanSchedule(request: {
  source: 'Quote' | 'WorkOrder';
  sourceKey: string;
  scenarioKey: string;
  strategy: AutoScheduleStrategy;
  projectStart: string;
}): Promise<ReplanResponse> {
  try {
    const response = await apiClient.post<ReplanResponse>('/schedule/replan', request);
    return response.data;
  } catch (err: unknown) {
    const locked = toScenarioLockedError(err); // P2-8: human message + remedy
    if (locked) throw locked;
    throw err;
  }
}

/** POST /api/schedule/restore-placement — the Undo for the last re-plan. */
export async function restorePlacement(request: {
  source: 'Quote' | 'WorkOrder';
  sourceKey: string;
  scenarioKey: string;
  undo: ReplanUndo;
}): Promise<{ restored: number; assignmentsRestored: number }> {
  try {
    const response = await apiClient.post<{ restored: number; assignmentsRestored: number }>(
      '/schedule/restore-placement',
      request,
    );
    return response.data;
  } catch (err: unknown) {
    // P2-8: replaying an undo token into a since-Submitted plan 409s
    // scenario_locked (P2-2) — show the "Reopen it…" remedy verbatim.
    const locked = toScenarioLockedError(err);
    if (locked) throw locked;
    throw err;
  }
}
